import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { auditLogService } from '@/services/auditLogService';
import { complianceService } from './complianceService';
import { locationService } from './locationService';

export interface EvidenceFieldDefinition {
  key: string; // Stable identifier (e.g., "recordId", "evidenceDate", "certificateNumber")
  label: string; // User-facing label (e.g., "AFR Certificate ID", "Evidence Date")
  type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list'; // Field type (list = array of strings, e.g. guarantor names)
  required: boolean; // Whether this field is required
  fileNameKey?: string; // DEPRECATED: Use fileNameKeys instead. If provided, extract value from file name by matching this key (e.g., "loan-number" matches "loan-number-12345.pdf")
  fileNameKeys?: string[]; // If provided, extract value from files matching any of these keys. Checks in order - first match wins. (e.g., ["loan-number", "application-number"])
  enumValues?: string[]; // Allowed values if type is 'enum'
  workflowRules?: string; // Natural language workflow rules (e.g., "This field is required if deliveryMethod is EMAIL"). When set, value is derived from workflow + asset facts only (not extracted from documents).
  extractionHints?: {
    keywords?: string[]; // Keywords to look for in documents
    anchors?: string[]; // Anchor text that appears near the value
    regex?: string; // Regex pattern to extract value
  };
  normalizationRules?: {
    dateFormat?: string; // Expected date format (e.g., "YYYY-MM-DD", "MM/DD/YYYY")
    trim?: boolean; // Whether to trim whitespace
    uppercase?: boolean; // Whether to convert to uppercase
    lowercase?: boolean; // Whether to convert to lowercase
  };
  validationRules?: {
    min?: number; // Minimum value (for number type)
    max?: number; // Maximum value (for number type)
    minLength?: number; // Minimum length (for string type)
    maxLength?: number; // Maximum length (for string type)
    pattern?: string; // Regex pattern for validation
    mustBeLessThan?: string; // Reference to another field key (e.g., "closingDate")
    mustBeGreaterThan?: string; // Reference to another field key
    customValidation?: string; // Custom validation rule description
  };
}

export interface ChecklistItemEvidenceDefinition {
  evidenceName: string; // Name/label for this evidence (e.g., "Flood Determination", "Joint Intent", etc.)
  required: boolean; // Whether this evidence is required or optional
  evidenceType?: 'FILE' | 'SYSTEM_RECORD' | 'DATA_ONLY' | 'ATTESTATION'; // Preferred evidence type (optional, auto-populated by backend)
  source?: 'UPLOAD' | 'CORE_SYSTEM' | 'DOC_SYSTEM' | 'FLOOD_VENDOR' | 'APPRAISAL_PLATFORM' | 'MANUAL' | 'OTHER'; // Preferred source (optional, auto-populated by backend)
  description?: string; // Helper text for this evidence requirement
  workflowRules?: string; // Natural language workflow rules (e.g., "This evidence is required if property is in SFHA")
  fieldDefinitions?: EvidenceFieldDefinition[]; // Fields to collect when evidence is uploaded
}

export interface ChecklistItem {
  id?: string;
  text: string;
  order: number;
  evidenceDefinitions?: ChecklistItemEvidenceDefinition[]; // Expected evidence for this item - fields are defined here
  autoCompleteRules?: Array<{
    condition: string; // JavaScript expression evaluated against facts
    status: 'PASS' | 'NA';
    reason: string; // Explanation for the status
  }>;
  confidenceThreshold?: number; // Default confidence threshold for this item (0-1, default 0.8)
}

export interface ChecklistVariable {
  name: string;
  possibleValues: string[]; // List of allowed values for this variable
}

export interface Checklist {
  id?: string;
  name: string;
  description?: string;
  items: ChecklistItem[];
  variables?: ChecklistVariable[]; // Variable definitions with possible values
  // Legacy support: keep string[] for backwards compatibility
  // If variables is string[], convert on load
  templateId?: string; // Associated compliance template ID
  locationIds?: string[]; // Array of location IDs - if empty/null/undefined, available for all locations (backward compatible)
  assetTypeIds?: string[]; // Array of asset type IDs - if empty/null/undefined, available for all asset types (backward compatible)
  organizationId?: string; // Multi-tenant support
  createdAt?: any;
  updatedAt?: any;
}

export const checklistService = {
  // Get all checklists for an organization
  getAllChecklists: async (organizationId?: string) => {
    try {
      const checklistsRef = collection(db, 'checklists');
      
      // Build query with organization filter if provided
      let q;
      if (organizationId) {
        // Filter by organizationId without orderBy to avoid index requirement
        q = query(
          checklistsRef,
          where('organizationId', '==', organizationId)
        );
      } else {
        q = query(checklistsRef, orderBy('name'));
      }
      
      const querySnapshot = await getDocs(q);
      const checklists: Checklist[] = [];
      querySnapshot.forEach((doc) => {
        checklists.push({ id: doc.id, ...doc.data() } as Checklist);
      });
      
      // Sort in memory after fetching to avoid index requirement
      if (organizationId) {
        checklists.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
      
      return { success: true, data: checklists };
    } catch (error: any) {
      console.error('Error getting all checklists:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single checklist by ID
  getChecklist: async (id: string) => {
    try {
      const checklistRef = doc(db, 'checklists', id);
      const docSnap = await getDoc(checklistRef);
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as Checklist };
      } else {
        return { success: false, error: 'Checklist not found' };
      }
    } catch (error: any) {
      console.error('Error getting checklist:', error);
      return { success: false, error: error.message };
    }
  },

  // Recursively remove undefined values from an object (Firestore doesn't accept undefined)
  removeUndefined: function removeUndefined(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (Array.isArray(obj)) {
      const cleaned = obj.map(item => removeUndefined(item)).filter(item => item !== undefined && item !== null);
      return cleaned.length > 0 ? cleaned : undefined;
    }
    if (typeof obj === 'object' && obj.constructor === Object) {
      const cleaned: any = {};
      let hasProperties = false;
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          const cleanedValue = removeUndefined(value);
          if (cleanedValue !== undefined && cleanedValue !== null) {
            cleaned[key] = cleanedValue;
            hasProperties = true;
          }
        }
      }
      return hasProperties ? cleaned : undefined;
    }
    return obj;
  },

  // Add a new checklist
  addChecklist: async (
    checklistData: Omit<Checklist, 'id'>, 
    organizationId?: string,
    userInfo?: { id: string; name: string; email: string }
  ) => {
    try {
      const checklistsRef = collection(db, 'checklists');
      
      // Recursively remove undefined fields from checklistData to avoid Firestore errors
      const cleanedData = checklistService.removeUndefined(checklistData);
      
      const docRef = await addDoc(checklistsRef, {
        ...cleanedData,
        ...(organizationId && { organizationId }), // Add organizationId if provided
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'checklist_added',
          entityType: 'checklist',
          entityId: docRef.id,
          entityName: checklistData.name,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      // Trigger field aggregation for associated asset types (async, don't wait)
      // Asset types can be linked either directly (assetTypeIds) or via compliance template (templateId)
      if (organizationId) {
        const { assetService } = await import('@/firebase/assetService');
        const { aggregateFieldsForAssetType } = await import('@/services/assetTypeFieldsService');
        
        const assetTypeIdsToAggregate = new Set<string>();
        
        // 1. Add directly linked asset types
        if (checklistData.assetTypeIds && checklistData.assetTypeIds.length > 0) {
          checklistData.assetTypeIds.forEach(id => assetTypeIdsToAggregate.add(id));
        }
        
        // 2. Find asset types linked via compliance template
        if (checklistData.templateId) {
          try {
            const assetTypesResult = await assetService.getAllAssetTypes(organizationId);
            if (assetTypesResult.success && assetTypesResult.data) {
              assetTypesResult.data.forEach(assetType => {
                if (assetType.complianceTemplateIds?.includes(checklistData.templateId!)) {
                  assetTypeIdsToAggregate.add(assetType.id!);
                }
              });
            }
          } catch (err) {
            console.error('[ChecklistService] Error finding asset types via template:', err);
          }
        }
        
        if (assetTypeIdsToAggregate.size > 0) {
          console.log(`[ChecklistService] Triggering field aggregation for ${assetTypeIdsToAggregate.size} asset type(s) after adding checklist (${checklistData.assetTypeIds?.length || 0} direct, ${assetTypeIdsToAggregate.size - (checklistData.assetTypeIds?.length || 0)} via template)`);
          assetTypeIdsToAggregate.forEach(assetTypeId => {
            aggregateFieldsForAssetType(assetTypeId, organizationId)
              .then(result => {
                if (result.success) {
                  console.log(`[ChecklistService] ✅ Successfully aggregated ${result.fieldsCount} fields from ${result.checklistsCount} checklists for asset type ${assetTypeId}`);
                } else {
                  console.error(`[ChecklistService] ❌ Failed to aggregate fields for asset type ${assetTypeId}:`, result.errors);
                }
              })
              .catch(err => {
                console.error(`[ChecklistService] ❌ Error aggregating fields for asset type ${assetTypeId}:`, err);
              });
          });
        } else {
          console.warn(`[ChecklistService] ⚠️ Skipping field aggregation: no asset types found (assetTypeIds=${checklistData.assetTypeIds?.length || 0}, templateId=${checklistData.templateId || 'none'})`);
        }
      }
      
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error adding checklist:', error);
      return { success: false, error: error.message };
    }
  },

  // Update an existing checklist
  updateChecklist: async (
    id: string, 
    checklistData: Partial<Checklist>,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      const checklistRef = doc(db, 'checklists', id);
      
      // Get existing checklist to preserve Spanish translation fields and for audit log
      const existingDoc = await getDoc(checklistRef);
      if (!existingDoc.exists()) {
        return { success: false, error: 'Checklist not found' };
      }
      
      const existingData = existingDoc.data();
      const currentChecklist = { id, ...existingData } as Checklist;
      const existingItems = (existingData.items || []) as any[];
      
        // If items are being updated, preserve Spanish translation fields
        let updatedItems = checklistData.items;
        if (updatedItems && existingItems.length > 0) {
          // Create a map of existing items by text (for matching)
          const existingItemsMap = new Map<string, any>();
          existingItems.forEach((item: any) => {
            const key = typeof item.text === 'string' ? item.text.trim() : '';
            if (key) {
              existingItemsMap.set(key, item);
            }
          });
          
          // Merge updated items with existing Spanish translations
          updatedItems = updatedItems.map((updatedItem: ChecklistItem, index: number) => {
            const itemText = updatedItem.text.trim();
            const existingItem = existingItemsMap.get(itemText);
            
            // If we found a matching existing item, preserve all its fields including Spanish translations
            if (existingItem) {
              // Preserve all fields from existing item, then override with updated fields
              // This ensures Spanish translations (text_es) are preserved if they exist
              // BUT preserve the updated evidenceDefinitions and fieldDefinitions from updatedItem
              const mergedItem: any = {
                ...existingItem, // Preserves all fields including text_es, translationSourceText_es, etc.
                text: updatedItem.text, // Override with updated text
                order: updatedItem.order, // Override with updated order
                // CRITICAL: Preserve updated evidenceDefinitions (which contain updated fieldDefinitions)
                evidenceDefinitions: updatedItem.evidenceDefinitions,
              };
              
              // If text changed, remove translationSourceText_es so Cloud Function knows to regenerate
              // Use delete instead of setting to undefined (Firestore doesn't accept undefined)
              if (updatedItem.text.trim() !== (existingItem.text || '').toString().trim()) {
                delete mergedItem.translationSourceText_es;
              }
              
              return mergedItem;
            }
            // New or changed item - check if there's a similar item in existing list
            // (in case text was slightly modified but we want to preserve translations)
            const similarExistingItem = existingItems.find((item: any) => {
              const existingText = (item.text || '').trim();
              // Check if texts are very similar (could be a minor edit)
              return existingText && itemText && 
                     (existingText.toLowerCase() === itemText.toLowerCase() || 
                      existingText.includes(itemText) || 
                      itemText.includes(existingText));
            });
            
            if (similarExistingItem && (similarExistingItem.text_es || similarExistingItem.translationSourceText_es)) {
              // Preserve translations from similar item, but keep updated evidenceDefinitions
              return {
                ...updatedItem,
                text_es: similarExistingItem.text_es,
                translationSourceText_es: similarExistingItem.translationSourceText_es,
                translationUpdatedAt_es: similarExistingItem.translationUpdatedAt_es,
                // CRITICAL: Ensure evidenceDefinitions from updatedItem are preserved
                evidenceDefinitions: updatedItem.evidenceDefinitions,
              };
            }
            
            // New item - Cloud Function will generate translations
            // evidenceDefinitions are already in updatedItem, so return as-is
            return updatedItem;
          });
        }
      
      // Prepare update data with preserved Spanish fields
      // First, ensure updatedItems doesn't have undefined values
      if (updatedItems) {
        updatedItems = checklistService.removeUndefined(updatedItems) as ChecklistItem[];
      }
      
      // Recursively remove undefined fields to avoid Firestore errors
      const cleanedChecklistData = checklistService.removeUndefined(checklistData);
      
      // Debug: Log the cleaned data to see if nested objects are preserved
      console.log('[checklistService] Cleaned checklist data:', JSON.stringify(cleanedChecklistData, null, 2));
      if (cleanedChecklistData && cleanedChecklistData.items) {
        cleanedChecklistData.items.forEach((item: any, itemIdx: number) => {
          item.evidenceDefinitions?.forEach((evDef: any, evIdx: number) => {
            evDef.fieldDefinitions?.forEach((fieldDef: any, fieldIdx: number) => {
              if (fieldDef.key === 'recipientNames') {
                console.log('[checklistService] recipientNames field in cleaned data:', {
                  key: fieldDef.key,
                  extractionHints: fieldDef.extractionHints,
                  normalizationRules: fieldDef.normalizationRules,
                  validationRules: fieldDef.validationRules
                });
              }
            });
          });
        });
      }
      
      // Build update data, ensuring no undefined values
      const updateData: any = {
        ...(cleanedChecklistData || {}),
        updatedAt: serverTimestamp(),
      };
      
      // Only add items if they exist and are not undefined
      if (updatedItems && updatedItems.length > 0) {
        updateData.items = updatedItems;
      } else if (cleanedChecklistData && cleanedChecklistData.items) {
        updateData.items = cleanedChecklistData.items;
      }
      
      // Final cleanup pass to ensure no undefined values
      const finalUpdateData = checklistService.removeUndefined(updateData);
      
      // Debug: Log final update data
      console.log('[checklistService] Final update data items:', JSON.stringify(finalUpdateData.items, null, 2));
      
      // Check for any undefined values before sending to Firestore
      const checkForUndefined = (obj: any, path: string = ''): void => {
        if (obj === undefined) {
          console.error(`[checklistService] Found undefined at path: ${path}`);
          throw new Error(`Undefined value found at path: ${path}`);
        }
        if (Array.isArray(obj)) {
          obj.forEach((item, index) => checkForUndefined(item, `${path}[${index}]`));
        } else if (obj && typeof obj === 'object' && obj.constructor === Object) {
          for (const [key, value] of Object.entries(obj)) {
            checkForUndefined(value, path ? `${path}.${key}` : key);
          }
        }
      };
      
      try {
        checkForUndefined(finalUpdateData);
      } catch (error) {
        console.error('[checklistService] Error checking for undefined values:', error);
        throw error;
      }
      
      await updateDoc(checklistRef, finalUpdateData);
      
      // Log audit entries for changes
      if (userInfo && organizationId) {
        // Check for compliance change (templateId)
        if (checklistData.templateId !== undefined && checklistData.templateId !== currentChecklist.templateId) {
          // Get compliance template names
          let oldComplianceName = 'Unknown';
          let newComplianceName = 'Unknown';
          
          if (currentChecklist.templateId) {
            const oldTemplateResult = await complianceService.getTemplate(currentChecklist.templateId);
            if (oldTemplateResult.success && oldTemplateResult.data) {
              oldComplianceName = oldTemplateResult.data.name;
            }
          }
          
          if (checklistData.templateId) {
            const newTemplateResult = await complianceService.getTemplate(checklistData.templateId);
            if (newTemplateResult.success && newTemplateResult.data) {
              newComplianceName = newTemplateResult.data.name;
            }
          }
          
          await auditLogService.log({
            action: 'checklist_compliance_changed',
            entityType: 'checklist',
            entityId: id,
            entityName: checklistData.name || currentChecklist.name,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldComplianceId: currentChecklist.templateId,
              newComplianceId: checklistData.templateId,
              oldComplianceName: oldComplianceName,
              newComplianceName: newComplianceName,
            },
          });
        }
        
        // Check for location change (locationIds)
        if (checklistData.locationIds !== undefined) {
          const oldLocationIds = currentChecklist.locationIds || [];
          const newLocationIds = checklistData.locationIds || [];
          
          // Check if locationIds actually changed
          const oldIdsSorted = [...oldLocationIds].sort().join(',');
          const newIdsSorted = [...newLocationIds].sort().join(',');
          
          if (oldIdsSorted !== newIdsSorted) {
            // Get location names
            const locationNames: string[] = [];
            for (const locId of newLocationIds) {
              const locResult = await locationService.getLocation(locId);
              if (locResult.success && locResult.data) {
                locationNames.push(locResult.data.name);
              }
            }
            
            await auditLogService.log({
              action: 'checklist_location_changed',
              entityType: 'checklist',
              entityId: id,
              entityName: checklistData.name || currentChecklist.name,
              userId: userInfo.id,
              userName: userInfo.name,
              userEmail: userInfo.email,
              organizationId: organizationId,
              details: {
                oldLocationIds: oldLocationIds,
                newLocationIds: newLocationIds,
                locationIds: newLocationIds,
              },
            });
          }
        }
        
        // Log general update (if not just a compliance or location change)
        if ((checklistData.templateId === undefined || checklistData.templateId === currentChecklist.templateId) &&
            (checklistData.locationIds === undefined)) {
          await auditLogService.log({
            action: 'checklist_updated',
            entityType: 'checklist',
            entityId: id,
            entityName: checklistData.name || currentChecklist.name,
            userId: userInfo.id,
            userName: userInfo.name,
            userEmail: userInfo.email,
            organizationId: organizationId,
            details: {
              oldValue: currentChecklist,
              newValue: { ...currentChecklist, ...updateData },
            },
          });
        }
      }
      
      // The Cloud Function translateChecklistOnUpdate should trigger here
      // and regenerate any missing Spanish translations automatically
      
      // Trigger field aggregation for associated asset types (async, don't wait)
      // Asset types can be linked either directly (assetTypeIds) or via compliance template (templateId)
      if (organizationId) {
        const { assetService } = await import('@/firebase/assetService');
        const { aggregateFieldsForAssetType } = await import('@/services/assetTypeFieldsService');
        
        const assetTypeIds = new Set<string>();
        
        // 1. Add directly linked asset types (check both old and new)
        if (currentChecklist.assetTypeIds) {
          currentChecklist.assetTypeIds.forEach(id => assetTypeIds.add(id));
        }
        if (checklistData.assetTypeIds) {
          checklistData.assetTypeIds.forEach(id => assetTypeIds.add(id));
        }
        
        // 2. Find asset types linked via compliance template
        const templateId = checklistData.templateId || currentChecklist.templateId;
        if (templateId) {
          try {
            const assetTypesResult = await assetService.getAllAssetTypes(organizationId);
            if (assetTypesResult.success && assetTypesResult.data) {
              assetTypesResult.data.forEach(assetType => {
                if (assetType.complianceTemplateIds?.includes(templateId)) {
                  assetTypeIds.add(assetType.id!);
                }
              });
            }
          } catch (err) {
            console.error('[ChecklistService] Error finding asset types via template:', err);
          }
        }
        
        if (assetTypeIds.size > 0) {
          const directCount = Array.from(assetTypeIds).filter(id => 
            (currentChecklist.assetTypeIds || []).includes(id) || (checklistData.assetTypeIds || []).includes(id)
          ).length;
          console.log(`[ChecklistService] Triggering field aggregation for ${assetTypeIds.size} asset type(s) after updating checklist (${directCount} direct, ${assetTypeIds.size - directCount} via template)`);
          assetTypeIds.forEach(assetTypeId => {
            aggregateFieldsForAssetType(assetTypeId, organizationId)
              .then(result => {
                if (result.success) {
                  console.log(`[ChecklistService] ✅ Successfully aggregated ${result.fieldsCount} fields from ${result.checklistsCount} checklists for asset type ${assetTypeId}`);
                } else {
                  console.error(`[ChecklistService] ❌ Failed to aggregate fields for asset type ${assetTypeId}:`, result.errors);
                }
              })
              .catch(err => {
                console.error(`[ChecklistService] ❌ Error aggregating fields for asset type ${assetTypeId}:`, err);
              });
          });
        } else {
          console.warn(`[ChecklistService] ⚠️ Skipping field aggregation: no asset types found (assetTypeIds=${assetTypeIds.size}, templateId=${templateId || 'none'})`);
        }
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error updating checklist:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a checklist
  deleteChecklist: async (
    id: string,
    userInfo?: { id: string; name: string; email: string },
    organizationId?: string
  ) => {
    try {
      // Get checklist info before deletion for audit log
      let checklistName = 'Unknown';
      if (userInfo && organizationId) {
        const checklistResult = await checklistService.getChecklist(id);
        if (checklistResult.success && checklistResult.data) {
          checklistName = checklistResult.data.name;
        }
      }
      
      const checklistRef = doc(db, 'checklists', id);
      await deleteDoc(checklistRef);
      
      // Log audit entry
      if (userInfo && organizationId) {
        await auditLogService.log({
          action: 'checklist_deleted',
          entityType: 'checklist',
          entityId: id,
          entityName: checklistName,
          userId: userInfo.id,
          userName: userInfo.name,
          userEmail: userInfo.email,
          organizationId: organizationId,
        });
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error deleting checklist:', error);
      return { success: false, error: error.message };
    }
  },

  // Copy a checklist
  copyChecklist: async (id: string) => {
    try {
      const checklistRef = doc(db, 'checklists', id);
      const docSnap = await getDoc(checklistRef);
      if (docSnap.exists()) {
        const originalData = docSnap.data() as Checklist;
        const newChecklistData: Omit<Checklist, 'id'> = {
          ...originalData,
          name: `${originalData.name} (Copy)`,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const checklistsRef = collection(db, 'checklists');
        const newDocRef = await addDoc(checklistsRef, newChecklistData);
        return { success: true, id: newDocRef.id };
      } else {
        return { success: false, error: 'Checklist not found' };
      }
    } catch (error: any) {
      console.error('Error copying checklist:', error);
      return { success: false, error: error.message };
    }
  },
};

